import { useCallback, useContext, useState } from 'react';

import Typography from '@mui/material/Typography';
import MuiTextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';

import SearchIcon from '@mui/icons-material/Search';

import {
    Formik, Form, Field,
} from 'formik';
import styled from 'styled-components';
import { UserActions } from 'BMapsCmds';
import { App } from '../BMapsApp';
import ProteinContextProvider, { ProteinContext } from './common/ProteinContextProvider';
import ProteinSelect from './common/ProteinSelect';
import {
    useEventBrokerStateHandling, useCloseOnEscape,
} from './common/helpers/useEventBrokerSubscription';
import { withColorSchemeInfoStyled, useColorSchemeInfo } from '../redux/prefs/access';

// Color the query text field against the (often dark) overlay background.
const StyledTextField = withColorSchemeInfoStyled(styled(MuiTextField)`
  ${({ $colorSchemeInfo: { textCss: color } }) => `
        & label {
            color: ${color};
            &.Mui-focused {
                color: ${color};
            }
        }
        & input {
            color: ${color};
        }
        & .MuiInput-underline:after {
            border-bottom-color: ${color};
        }
        & .MuiOutlinedInput-root {
            & fieldset {
                border-color: ${color};
            }
            &:hover fieldset {
                border-color: ${color};
            }
            &.Mui-focused fieldset {
                border-color: ${color};
            }
        }
    `}
`);

// Same colors as the query field, applied to the picker via MUI `sx` (emotion) so the
// label (default + focused), the selected value text, the dropdown arrow, and the outline
// all track the scheme's text color. Styling the picker through styled-components did not
// win the cascade for the InputLabel, so we use MUI's own styling API here.
const proteinSelectSx = (color) => ({
    marginBottom: '0.5em',
    '& .MuiInputLabel-root': { color },
    '& .MuiInputLabel-root.Mui-focused': { color },
    '& .MuiSelect-select': { color },
    '& .MuiSvgIcon-root': { color },
    '& .MuiOutlinedInput-root': {
        '& fieldset': { borderColor: color },
        '&:hover fieldset': { borderColor: color },
        '&.Mui-focused fieldset': { borderColor: color },
    },
});

export default function SelectBox() {
    const [open, setOpen] = useState(false);
    // Remember the picked protein across open/close (this component stays mounted; the
    // ProteinContextProvider subtree below is unmounted each time the box closes).
    const [rememberedProtein, setRememberedProtein] = useState(null);

    const getNewOpen = useCallback(
        (evtArg, oldOpen) => evtArg === true || (evtArg === 'toggle' && !oldOpen),
        []
    );
    useEventBrokerStateHandling('openSelectBox', open, setOpen, getNewOpen);
    useCloseOnEscape(setOpen);
    const { css, textCss, controlAlphaCss } = useColorSchemeInfo();

    const style = {
        position: 'absolute',
        right: 0,
        bottom: 0,
        backgroundColor: css + controlAlphaCss,
        paddingLeft: '0.5em',
        paddingTop: '0.5em',
        paddingBottom: 'var(--mol-button-group-border)',
    };

    // Restore the remembered protein only if it is still loaded; otherwise fall back to the
    // first protein (defaultToFirstProtein). This runs on every render, before the open guard,
    // and App is not initialized during SSR/prerender, so gate on App.Ready. getLoadedProteins
    // returns stable MapCase objects, so an identity check is valid.
    const loadedProteins = App.Ready ? App.Workspace.getLoadedProteins() : [];
    const initialProtein = loadedProteins.includes(rememberedProtein) ? rememberedProtein : null;

    // ProteinContextProvider supplies the loaded proteins and tracks the picked one.
    // initialProtein restores the last pick; defaultToFirstProtein resolves a target up front
    // so a query can be submitted without first interacting with the picker.
    // Gate on App.Ready: ProteinContextProvider reads App.Workspace unconditionally, which is
    // undefined before app init (e.g., if openSelectBox somehow fires early / during prerender).
    return open && App.Ready && (
        <Typography component="div" style={style}>
            <ProteinContextProvider initialProtein={initialProtein} defaultToFirstProtein>
                <SelectBoxForm
                    setOpen={setOpen}
                    textCss={textCss}
                    onProteinChange={setRememberedProtein}
                />
            </ProteinContextProvider>
        </Typography>
    );
}

function SelectBoxForm({ setOpen, textCss, onProteinChange }) {
    // selectedCaseData follows the protein picked in ProteinSelect (which writes to context).
    const { selectedCaseData, loadedProteins, lookupCaseData } = useContext(ProteinContext);

    const handleSubmit = useCallback(async ({ query }) => {
        // Empty input: keep the box open and send nothing (matches existing behavior).
        if (!query) return;
        setOpen(false);

        // Target a specific protein only when more than one is loaded. With 0 or 1 protein,
        // App.ServerConnection is the only connection, so submit with no target to preserve
        // the existing routing exactly.
        if (loadedProteins.length > 1 && selectedCaseData) {
            // There is no UI for managing selections across multiple proteins, so a targeted
            // selection is exclusive: clear any selection on the other loaded proteins first,
            // then select on the chosen one LAST (so its solute ends active in multi-protein
            // mode). UserActions.Select(..., caseData) routes each call to the right connector
            // (selectivity) or activates that solute first (multi-protein).
            const otherCaseData = loadedProteins
                .map((protein) => lookupCaseData(protein))
                .filter((caseData) => caseData && caseData !== selectedCaseData);
            for (const caseData of otherCaseData) {
                // Sequential on purpose: in multi-protein mode these share one connector and
                // each switches the active solute, so they must not interleave.
                await UserActions.Select('none', caseData); // eslint-disable-line no-await-in-loop
            }
            await UserActions.Select(query, selectedCaseData);
        } else {
            await UserActions.Select(query);
        }
    }, [loadedProteins, selectedCaseData, lookupCaseData, setOpen]);

    return (
        <Formik initialValues={{ query: '' }} onSubmit={handleSubmit}>
            <Form>
                {/* ProteinSelect renders null unless 2+ proteins are loaded, so the
                    single-/zero-protein SelectBox is unchanged. It sits on its own row
                    above the query input (the box grows upward into the canvas). */}
                <ProteinSelect
                    id="select-box_protein-select"
                    label="Structure to query"
                    sx={proteinSelectSx(textCss)}
                    onChange={(evt, { protein } = {}) => onProteinChange(protein)}
                />
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <Field // Note: We don't need an error area, so don't use common/TextField
                        as={StyledTextField}
                        name="query"
                        label="Select atoms query"
                        autoFocus
                    />
                    {/* A real submit button (not FormikIconSubmit, which is type="button")
                        so Enter submits reliably now that the form has more than one field. */}
                    <IconButton type="submit" aria-label="Run selection query">
                        <SearchIcon style={{ color: textCss }} />
                    </IconButton>
                </div>
            </Form>
        </Formik>
    );
}
